import { useState, useEffect, useCallback } from "react";
import { subscribeHeaderBadges } from "@/lib/supabase/header-badge-subscription";
import { useLocation } from "wouter";
import { PublicNavigationLink as Link } from "./PublicNavigationLink";
import {
  Menu,
  LogOut,
  Heart,
  Settings,
  Car,
  CheckCircle2,
  Briefcase,
  User,
  HelpCircle,
  ArrowRightLeft,
  MessageSquare,
  Bell,
  ClipboardList,
  DollarSign,
  LayoutDashboard,
} from "lucide-react";
import { Logo } from "@/components/platform/Logo";
import { useUser } from "@/hooks/useUser";
import { accountNavigationSession } from "@/lib/account-navigation-session";
import { useAuthModal } from "@/components/auth/AuthModalContext";
import { Sentry } from "@/lib/sentry";
import { fetchUnreadMessageCount, fetchUnreadNotificationCount, fetchFavouriteIds, isMissingProfileError } from "@/lib/users-api";
import { onNotificationsChanged } from "@/lib/notifications-events";
import { onMessagesChanged, onConversationReadAcrossTabs } from "@/lib/messages-events";
import { getSupabaseClient } from "@/lib/supabase/client";
import { Avatar, AvatarImage, AvatarFallback } from "@workspace/ui/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuGroup,
} from "@workspace/ui/components/ui/dropdown-menu";
import { Sheet } from "@workspace/ui/components/ui/sheet";
import { MobileNavigationSheetContent } from "./MobileNavigationSheet";
import { cn } from "@/lib/utils";
import { USER_PLACEHOLDER_AVATAR_URL } from "@/lib/site-assets";
import { useTranslation } from "react-i18next";
import { LanguageRegionModal as LanguageSwitcher } from "@/components/layout/LanguageRegionModal";
import { NAVBAR_SCROLL_THRESHOLD } from "@/lib/design-tokens";
import { useFeatureFlag } from "@/hooks/useFeatureFlags";
import { useAppMode } from "@/contexts/AppModeContext";
import { usePlatform } from "@/hooks/usePlatform";
import { HeaderSearch } from "@/components/layout/HeaderSearch";
import { PRIMARY_NAVIGATION } from "@/components/layout/navigation";
import { useRouter } from "wouter";
import { publicHomeHref } from "@/lib/public-navigation-href";

function getPageScrollTop(): number {
  const documentScrollTop = document.scrollingElement?.scrollTop ?? 0;
  const documentElementScrollTop = document.documentElement?.scrollTop ?? 0;
  const bodyScrollTop = document.body?.scrollTop ?? 0;
  return Math.max(window.scrollY, documentScrollTop, documentElementScrollTop, bodyScrollTop);
}

// Mobile chrome strategy. Phones use ONE consistent solid brand-green
// header across the whole app (white logo + white menu icon) so the mobile
// experience is uniform — no more white-browse vs green-account split. The
// homepage hero keeps its transparent overlay while at the top. Desktop
// (md+) is unchanged: slim white header.

export function Header() {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [location, navigate] = useLocation();
  const homeHref = publicHomeHref(useRouter().base);
  const { user: authUser, profile, isAdmin, session: authSession, signOut } = useUser();
  const session = accountNavigationSession(authSession);
  const user = session ? authUser : null;
  const { mode, toggle, canHost } = useAppMode();
  const authModal = useAuthModal();
  const platform = usePlatform();
  // Task #1236 — the header "Help Centre" entry now navigates to the new
  // `/help` knowledge base instead of opening Intercom directly. The
  // landing page itself surfaces the chat CTA, which keeps a single
  // canonical entry point and lets users self-serve before chatting.
  const openHelpCentre = useCallback(() => {
    setMenuOpen(false);
    setMobileOpen(false);
    navigate("/help");
  }, [navigate]);
  const isHost = !!profile?.is_host;
  const referralsEnabled = useFeatureFlag("feature_referrals");
  const [unreadMessages, setUnreadMessages] = useState(0);
  // Task #2502: most-recently-active conversation id, used in Personal
  // (guest) mode to deep-link the messages icon to the user's active
  // thread. Renters don't see the inbox master/detail UI, so without
  // this the icon would silently bounce to /trips.
  const [latestConversationId, setLatestConversationId] = useState<string | null>(null);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [favouritesCount, setFavouritesCount] = useState(0);
  const [scrolled, setScrolled] = useState(false);

  const isHomepage = location === "/" || location === "";
  const isTransparent = isHomepage && !scrolled && !mobileOpen;
  // The green chrome is intentionally a mobile-web treatment. Keep the
  // desktop header on the original white surface even when the mobile
  // header is in its solid state.
  const useBrandChrome = platform.platform === "driveshare" && !isTransparent;
  // Task #1083: the header CTA mirrors the persisted mode (not the URL),
  // so that flipping the toggle and landing on a non-/dashboard host page
  // (or vice-versa) still shows the right "Switch to..." label.
  const inHostMode = canHost && mode === "host";

  const handleToggleMode = () => {
    if (!canHost) return;
    setMobileOpen(false);
    toggle();
    navigate(mode === "host" ? "/trips" : "/dashboard");
  };

  // Task #7901 — the scroll listener now runs on every page (not just the
  // homepage): besides the homepage transparent→solid flip it drives the
  // scroll-edge treatment, where the header's divider/shadow appears only
  // once content has actually scrolled underneath it. Off the homepage the
  // threshold is a few px so the edge shows as soon as content overlaps.
  useEffect(() => {
    const threshold = isHomepage ? NAVBAR_SCROLL_THRESHOLD : 4;
    const handleScroll = () => {
      setScrolled(getPageScrollTop() > threshold);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    // iOS Safari and embedded previews can dispatch page scrolling through
    // the document scroll root instead of updating window.scrollY first.
    // Capture the document event too, while the max() helper avoids changing
    // state for unrelated nested scrolling panels.
    document.addEventListener("scroll", handleScroll, { passive: true, capture: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("scroll", handleScroll, { capture: true });
    };
  }, [isHomepage]);

  useEffect(() => {
    if (isHost) return;
    const img = new Image();
    img.src = `${import.meta.env.BASE_URL || "/"}images/become-a-host-car.webp`;
  }, [isHost]);

  const refreshCounts = useCallback(() => {
    if (!session?.access_token) return;
    // Task #1222 — `Authentication required` here is expected when the
    // Supabase token has just expired (e.g. tab open overnight); don't
    // forward those 401s to Sentry. Only escalate truly unexpected
    // failures.
    const isAuthError = (err: unknown): boolean => {
      const e = err as { status?: number; message?: string } | undefined;
      if (e?.status === 401) return true;
      if (typeof e?.message === "string" && /Authentication required/i.test(e.message)) return true;
      return false;
    };
    // Task #5417 — an incomplete-profile user (valid session, no `public.users`
    // row yet) gets a 404 "User profile not found" from these authed badge
    // calls. That's an expected onboarding state, not an error: treat it as
    // empty (0 counts) and don't report it to Sentry. The profile-completion
    // flow already surfaces the real state.
    const isExpected = (err: unknown): boolean =>
      isAuthError(err) || isMissingProfileError(err);
    fetchUnreadMessageCount(session.access_token).then((r) => {
      setUnreadMessages(r.count);
      setLatestConversationId(r.latest_conversation_public_id ?? null);
    }).catch((err) => {
      if (isMissingProfileError(err)) {
        setUnreadMessages(0);
        setLatestConversationId(null);
        return;
      }
      console.error("[Header] Failed to fetch unread message count:", err);
      if (!isExpected(err)) Sentry.captureException(err);
    });
    fetchUnreadNotificationCount(session.access_token).then((r) => setUnreadNotifications(r.count)).catch((err) => {
      if (isMissingProfileError(err)) {
        setUnreadNotifications(0);
        return;
      }
      console.error("[Header] Failed to fetch unread notification count:", err);
      if (!isExpected(err)) Sentry.captureException(err);
    });
    fetchFavouriteIds(session.access_token).then((ids) => setFavouritesCount(ids.length)).catch((err) => {
      if (isMissingProfileError(err)) {
        setFavouritesCount(0);
        return;
      }
      console.error("[Header] Failed to fetch favourite ids:", err);
      if (!isExpected(err)) Sentry.captureException(err);
    });
  }, [session?.access_token]);

  useEffect(() => {
    if (!session?.access_token || !user) return;
    refreshCounts();

    // Task #2425 — subscribe to in-app notification mutations so the
    // badge clears immediately even when Supabase realtime isn't
    // connected (no client, dropped websocket, missed event, etc.).
    const unsubscribeLocal = onNotificationsChanged(() => refreshCounts());
    // Inbox feedback (May 2026) — same idea for the messages badge.
    // Opening a conversation marks its messages read server-side; the
    // realtime subscription below is supposed to catch the resulting
    // UPDATE on `messages`, but users were reporting the red "1" still
    // showing while they were already viewing the thread. The local
    // pub/sub fires on every `fetchConversation`, so the badge clears
    // even when realtime didn't deliver the row (websocket dropped,
    // table not in the publication, RLS broadcast filtered, etc.).
    const unsubscribeMessagesLocal = onMessagesChanged(() => refreshCounts());
    // Task #4743 — reading a conversation in another tab broadcasts a
    // cross-tab signal; recount so the badge clears here too, even when this
    // tab isn't on the inbox route (the sidebar isn't mounted to catch it).
    const unsubscribeReadAcrossTabs = onConversationReadAcrossTabs(() => refreshCounts());

    const sb = getSupabaseClient();
    if (!sb) {
      const interval = setInterval(refreshCounts, 30000);
      return () => {
        clearInterval(interval);
        unsubscribeLocal();
        unsubscribeMessagesLocal();
        unsubscribeReadAcrossTabs();
      };
    }

    const unsubscribeRealtime = subscribeHeaderBadges(sb, user.id, refreshCounts);

    return () => {
      unsubscribeRealtime();
      unsubscribeLocal();
      unsubscribeMessagesLocal();
      unsubscribeReadAcrossTabs();
    };
  }, [session?.access_token, user, refreshCounts]);

  const initials = profile
    ? `${(profile.first_name || "")[0] || ""}${(profile.last_name || "")[0] || ""}`.toUpperCase() || "U"
    : "U";

  const hasUnread = unreadMessages > 0 || unreadNotifications > 0;

  // Task #2904: renters now have a real inbox at /messages, so the
  // header icon points there for both modes. The icon is shown
  // unconditionally so users can always reach the inbox (which renders
  // its own empty state when they have no conversations yet). Host
  // behavior is unchanged. The href stays relative so wouter's `base`
  // prefixes it with the active market/locale (e.g. /uk).
  // `latestConversationId` is still tracked for the unread badge logic
  // below but no longer drives the icon destination.
  void latestConversationId;
  const messagesHref: string | null = "/messages";

  const handleSignOut = async () => {
    await signOut();
    setMobileOpen(false);
  };

  // Base classes apply to the mobile header; the md: overrides restore the
  // desktop white-header palette whenever the mobile chrome is active.
  const lightChrome = isTransparent;
  const textMutedCls = isTransparent
    ? "text-white/85"
    : useBrandChrome
      ? "text-white/85 md:text-gray-700"
      : "text-gray-700";
  const textActiveCls = isTransparent
    ? "text-white"
    : useBrandChrome
      ? "text-white md:text-gray-900"
      : "text-gray-900";
  const iconMutedCls = isTransparent
    ? "text-white/80"
    : useBrandChrome
      ? "text-white/80 md:text-gray-500"
      : "text-gray-500";
  const hoverBgCls = isTransparent
    ? "hover:bg-white/10"
    : useBrandChrome
      ? "hover:bg-white/10 md:hover:bg-gray-100"
      : "hover:bg-gray-100";
  const compactActionIconCls = isTransparent
    ? "text-white/80"
    : "text-white/80 md:text-gray-500";
  const compactActionHoverCls = isTransparent
    ? "hover:bg-white/10"
    : "hover:bg-white/10 md:hover:bg-gray-100";
  const rightCtaIsToggle = !!user && isHost;
  const rightCtaHref = "/list-a-car";
  const rightCtaLabel = user && isHost
    ? (inHostMode ? t("nav.switch_to_guest") : t("nav.switch_to_hosting"))
    : t("nav.become_a_host");

  return (
    <header
      className={cn(
        // Task #3560/#3561 — own the top safe-area inset so the h-14/h-20
        // content row sits below the notch on standalone/notched phones.
        // env(safe-area-inset-top) is 0 on desktop + non-notch devices, so
        // this is a no-op there and desktop chrome is unchanged.
        // Task #7901 — the header now follows the bottom tab bar's translucent
        // material pattern (panel weight) instead of a fully opaque bar with a
        // permanent hard divider. Explicit color/shadow transition list (not
        // `transition-all`) so the fixed header never animates
        // layout-affecting properties on scroll; blur is a static property of
        // the material, never animated.
        "fixed top-0 z-50 w-full border-b transition-[background-color,border-color,box-shadow] duration-300 ease-in-out pt-[env(safe-area-inset-top)]",
        isTransparent
          ? "bg-transparent border-transparent"
          : cn(
              // Mobile: brand-green panel material. Translucent + blurred is
              // always solid on the active brand so scrolling away from the hero
              // produces an unmistakable green chrome state.
              "bg-brand",
              // Desktop: always restore the original light header surface;
              // the mobile treatment must never bleed into desktop widths.
              "md:bg-background",
              "md:border-gray-200",
              // Scroll edge: divider + shadow only once content has scrolled
              // underneath the header; at rest the edge is invisible.
              scrolled
                ? "border-brand shadow-sm md:border-gray-200"
                : "border-transparent md:border-transparent"
            )
      )}
    >
      <div className="mx-auto grid h-14 max-w-[1760px] grid-cols-[auto_1fr_auto] items-center gap-4 px-4 sm:px-6 md:h-20 lg:px-10">
        {/* Left: Logo — navigates home from any page; on the homepage
            (where re-navigating to "/" is a no-op) it scrolls back to
            the top so the click still has a visible effect. */}
        <Link
          href={homeHref}
          className="flex items-center touch-44"
          aria-label={t("nav.home_aria")}
          onClick={(e) => {
            if (isHomepage) {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
              setMobileOpen(false);
            }
          }}
        >
          {/* Desktop logo — hidden on mobile, shown from md up. The responsive
              hide lives on a WRAPPER span (not on the Logo itself) so it can't
              be overridden by the Logo's own `display`. Putting `hidden`
              alongside an unscoped display utility on the same element is
              unreliable — Tailwind emits `inline-flex` after `hidden`, so the
              logo would never hide and BOTH instances rendered at once (the
              duplicate-logo-in-header bug). `display:none` on this wrapper
              hides the whole subtree regardless of the child's display. */}
          <span className="hidden md:inline-flex">
            <Logo size="md" variant={lightChrome ? "light" : "dark"} />
          </span>
          {/* Mobile logo — slim, sized to match the host header. The mobile
              header is now always solid green (or the transparent hero), so
              the logo is always the light/white variant. Same wrapper pattern. */}
          <span className="md:hidden [&_img]:!h-8 [&_img]:!w-auto">
            <Logo size="md" variant="light" />
          </span>
        </Link>

        {/* Center: original compact desktop navigation. */}
        <nav className="hidden items-center justify-center md:flex" aria-label={t("nav.primary_aria")}>
          <ul className="flex items-center gap-2 lg:gap-4">
            {PRIMARY_NAVIGATION.map((link) => {
              const isActive = location === link.href;
              return (
                <li key={link.id} className="relative">
                  <Link
                    href={link.href}
                    className={cn(
                      "group relative flex items-center justify-center whitespace-nowrap rounded-2xl px-4 py-2 text-sm font-medium leading-none tracking-tight transition-colors duration-200",
                      isActive ? textActiveCls : textMutedCls,
                      hoverBgCls,
                    )}
                  >
                    <span className="relative leading-none">{t(link.labelKey, link.fallback)}</span>
                    <span
                      className={cn(
                        "absolute inset-x-4 bottom-0 h-px rounded-full transition-opacity duration-200",
                        isActive ? "opacity-100" : "opacity-0",
                        lightChrome ? "bg-white" : "bg-gray-900",
                      )}
                    />
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Right: original desktop actions; keep the mobile search launcher. */}
        <div className="flex items-center justify-self-end gap-2">
          <HeaderSearch className={cn("md:hidden", compactActionIconCls, compactActionHoverCls)} />
          <div className="hidden items-center gap-2 md:flex">
          {rightCtaIsToggle ? (
            <button
              type="button"
              onClick={handleToggleMode}
              className={cn(
                "rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200",
                textMutedCls,
                hoverBgCls,
              )}
            >
              {rightCtaLabel}
            </button>
          ) : (
            <Link
              href={rightCtaHref}
              className={cn(
                "rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200",
                textMutedCls,
                hoverBgCls,
              )}
            >
              {rightCtaLabel}
            </Link>
          )}

          {user ? (
            <Link
              href="/favourites"
              aria-label={t("nav.favourites")}
              title={t("nav.favourites")}
              className={cn(
                "relative flex h-10 w-10 items-center justify-center rounded-full transition-colors",
                hoverBgCls
              )}
            >
              <Heart className={cn("h-5 w-5", iconMutedCls)} strokeWidth={1.75} />
              {favouritesCount > 0 && (
                <span className="absolute right-1 top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                  {favouritesCount > 9 ? "9+" : favouritesCount}
                </span>
              )}
            </Link>
          ) : (
            <button
              type="button"
              aria-label={t("nav.favourites")}
              title={t("nav.favourites")}
              onClick={() =>
                authModal.open({
                  intent: "signin",
                  next: "/favourites",
                })
              }
              className={cn(
                "relative flex h-10 w-10 items-center justify-center rounded-full transition-colors",
                hoverBgCls
              )}
            >
              <Heart className={cn("h-5 w-5", iconMutedCls)} strokeWidth={1.75} />
            </button>
          )}

          {user && messagesHref && (
            <Link
              href={messagesHref}
              aria-label={t("nav.messages")}
              className={cn(
                "relative flex h-10 w-10 items-center justify-center rounded-full transition-colors",
                hoverBgCls
              )}
            >
              <MessageSquare className={cn("h-5 w-5", iconMutedCls)} strokeWidth={1.75} />
              {unreadMessages > 0 && (
                <span className="absolute right-1 top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                  {unreadMessages > 9 ? "9+" : unreadMessages}
                </span>
              )}
            </Link>
          )}

          <LanguageSwitcher
            iconClassName={iconMutedCls}
            buttonClassName={hoverBgCls}
            align="end"
          />

          <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
            <DropdownMenuTrigger asChild>
              <button
                aria-label={t("nav.toggle_menu")}
                className={cn(
                  "relative ml-1 flex items-center gap-2 rounded-full border bg-white py-1.5 pl-3 pr-1.5 shadow-sm transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                  isTransparent ? "border-white/40" : "border-gray-200"
                )}
              >
                <Menu className="h-4 w-4 text-gray-700" strokeWidth={2} />
                <span className="relative">
                  <Avatar className="h-7 w-7">
                    {user && profile?.profile_image && (
                      <AvatarImage src={profile.profile_image} alt={profile.first_name || "User"} />
                    )}
                    <AvatarFallback className={cn(
                      "text-[11px] font-semibold",
                      user ? "bg-brand text-white" : "bg-gray-200 text-gray-500"
                    )}>
                      {user ? initials : (
                        <img
                          src={USER_PLACEHOLDER_AVATAR_URL}
                          alt=""
                          aria-hidden="true"
                          className="h-7 w-7"
                        />
                      )}
                    </AvatarFallback>
                  </Avatar>
                  {user && hasUnread && (
                    <span
                      className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white"
                      role="status"
                      aria-label={t("nav.unread_indicator_aria", { count: unreadMessages + unreadNotifications })}
                    />
                  )}
                </span>
              </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end" sideOffset={8} className="w-64 rounded-xl shadow-lg border border-gray-200 p-0 overflow-hidden">
              {user ? (
                <>
                  {isHost && (
                    <>
                      <DropdownMenuGroup className="py-1">
                        <DropdownMenuItem
                          className="px-4 py-2.5 cursor-pointer"
                          onClick={(e) => {
                            // Prevent the menu's default close-then-navigate
                            // behaviour so handleToggleMode owns the redirect
                            // (mode flip + canonical landing for the new mode).
                            e.preventDefault();
                            setMenuOpen(false);
                            handleToggleMode();
                          }}
                        >
                          <div className="flex items-center gap-3">
                            <ArrowRightLeft className="h-[18px] w-[18px] text-gray-500" />
                            <span className="text-sm font-semibold">{inHostMode ? t("nav.switch_to_guest") : t("nav.switch_to_hosting")}</span>
                          </div>
                        </DropdownMenuItem>
                      </DropdownMenuGroup>
                      <DropdownMenuSeparator className="my-0" />
                    </>
                  )}

                  <DropdownMenuGroup className="py-1">
                    {/* Task #1083: account dropdown is mode-aware so the
                        arrow toggle remains the only in-app way to switch
                        modes. Guest-only links are hidden in host mode and
                        host-only links are hidden in guest mode. */}
                    {!inHostMode && (
                      <>
                        <DropdownMenuItem asChild className="px-4 py-2.5 cursor-pointer">
                          <Link href="/trips" className="flex items-center gap-3">
                            <Briefcase className="h-[18px] w-[18px] text-gray-500" />
                            <span className="text-sm">{t("nav.my_trips")}</span>
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild className="px-4 py-2.5 cursor-pointer">
                          <Link href="/favourites" className="flex items-center gap-3">
                            <Heart className="h-[18px] w-[18px] text-gray-500" />
                            <span className="text-sm">{t("nav.favourites")}</span>
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                    {inHostMode && (
                      <>
                        <DropdownMenuItem asChild className="px-4 py-2.5 cursor-pointer">
                          <Link href="/dashboard" className="flex items-center gap-3">
                            <LayoutDashboard className="h-[18px] w-[18px] text-gray-500" />
                            <span className="text-sm">{t("nav.host_dashboard")}</span>
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild className="px-4 py-2.5 cursor-pointer">
                          <Link href="/dashboard/bookings" className="flex items-center gap-3">
                            <ClipboardList className="h-[18px] w-[18px] text-gray-500" />
                            <span className="text-sm">{t("nav.bookings")}</span>
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild className="px-4 py-2.5 cursor-pointer">
                          <Link href="/dashboard/listings" className="flex items-center gap-3">
                            <Car className="h-[18px] w-[18px] text-gray-500" />
                            <span className="text-sm">{t("nav.my_garage", "My Garage")}</span>
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild className="px-4 py-2.5 cursor-pointer">
                          <Link href="/dashboard/earnings" className="flex items-center gap-3">
                            <DollarSign className="h-[18px] w-[18px] text-gray-500" />
                            <span className="text-sm">{t("nav.earnings")}</span>
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                    {messagesHref && (
                    <DropdownMenuItem asChild className="px-4 py-2.5 cursor-pointer">
                      <Link href={messagesHref} className="flex items-center justify-between gap-3">
                        <span className="flex items-center gap-3">
                          <MessageSquare className="h-[18px] w-[18px] text-gray-500" />
                          <span className="text-sm">{t("nav.messages")}</span>
                        </span>
                        {unreadMessages > 0 && (
                          <span className="flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white">
                            {unreadMessages > 9 ? "9+" : unreadMessages}
                          </span>
                        )}
                      </Link>
                    </DropdownMenuItem>
                    )}
                    <DropdownMenuItem asChild className="px-4 py-2.5 cursor-pointer">
                      {/* Task #1083: route to the notifications surface that
                          matches the user's persisted mode so a host in guest
                          mode (or a non-host) doesn't get silently kicked into
                          host URL space — the arrow toggle is the only
                          allowed mode switcher. */}
                      <Link href={inHostMode ? "/dashboard/notifications" : "/notifications"} className="flex items-center justify-between gap-3">
                        <span className="flex items-center gap-3">
                          <Bell className="h-[18px] w-[18px] text-gray-500" />
                          <span className="text-sm">{t("nav.notifications")}</span>
                        </span>
                        {unreadNotifications > 0 && (
                          <span className="flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white">
                            {unreadNotifications > 9 ? "9+" : unreadNotifications}
                          </span>
                        )}
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuGroup>

                  <DropdownMenuSeparator className="my-0" />

                  <DropdownMenuGroup className="py-1">
                    <DropdownMenuItem asChild className="px-4 py-2.5 cursor-pointer">
                      <Link href={inHostMode ? "/dashboard/profile" : "/profile"} className="flex items-center gap-3">
                        <User className="h-[18px] w-[18px] text-gray-500" />
                        <span className="text-sm">{inHostMode ? t("nav.host_profile") : t("nav.profile")}</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="px-4 py-2.5 cursor-pointer">
                      <Link href="/settings" className="flex items-center gap-3">
                        <Settings className="h-[18px] w-[18px] text-gray-500" />
                        <span className="text-sm">{t("nav.account_settings")}</span>
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuGroup>

                  {/* Become a host promo (non-hosts only) — sits between
                      Account settings and Help Centre per design tweak
                      May 2026. Previously rendered at the top of the user
                      menu; moved down so the primary nav items (Trips,
                      Favourites, Messages, Notifications, Profile,
                      Account settings) read first and the promo lives
                      next to the "next steps" cluster instead of
                      competing with the avatar. */}
                  {!isHost && (
                    <>
                      <DropdownMenuSeparator className="my-0" />
                      <div className="px-3 pt-3 pb-2">
                        <Link
                          href="/list-a-car"
                          className="block overflow-hidden rounded-xl border border-gray-200 bg-white transition-shadow hover:shadow-md"
                        >
                          <div className="flex items-center gap-2">
                            <div className="min-w-0 flex-1 py-3 pl-3">
                              <div className="flex items-center gap-1.5">
                                <Car className="h-4 w-4 shrink-0 text-brand" strokeWidth={1.75} />
                                <p className="text-sm font-bold text-foreground">{t("auth.become_host_title")}</p>
                              </div>
                              <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
                                {t("auth.become_host_desc")}
                              </p>
                            </div>
                            <img
                              src={`${import.meta.env.BASE_URL || "/"}images/become-a-host-car.webp`}
                              alt=""
                              aria-hidden="true"
                              className="h-14 w-20 shrink-0 object-contain"
                              loading="lazy"
                            />
                          </div>
                        </Link>
                      </div>
                    </>
                  )}

                  <DropdownMenuSeparator className="my-0" />

                  <DropdownMenuGroup className="py-1">
                    <DropdownMenuItem
                      onSelect={(e) => {
                        e.preventDefault();
                        openHelpCentre();
                      }}
                      className="px-4 py-2.5 cursor-pointer"
                      data-testid="header-help-centre"
                    >
                      <HelpCircle className="h-[18px] w-[18px] text-gray-500" />
                      <span className="text-sm">{t("nav.help_centre")}</span>
                    </DropdownMenuItem>
                  </DropdownMenuGroup>

                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator className="my-0" />
                      <DropdownMenuItem asChild className="px-4 py-2.5 cursor-pointer">
                        <Link href="/admin" className="flex items-center gap-3">
                          <CheckCircle2 className="h-[18px] w-[18px] text-emerald-600" />
                          <span className="text-sm">{t("nav.admin")}</span>
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}

                  <DropdownMenuSeparator className="my-0" />
                  <DropdownMenuItem onClick={handleSignOut} className="px-4 py-2.5 cursor-pointer">
                    <LogOut className="h-[18px] w-[18px] text-gray-500" />
                    <span className="text-sm">{t("nav.sign_out")}</span>
                  </DropdownMenuItem>
                </>
              ) : (
                <>
                  <div className="px-3 pt-3 pb-2">
                    <button
                      type="button"
                      onClick={() => {
                        setMenuOpen(false);
                        authModal.open({ intent: "signup" });
                      }}
                      className="block w-full text-left overflow-hidden rounded-xl border border-gray-200 bg-white transition-shadow hover:shadow-md"
                    >
                      <div className="flex items-center gap-2">
                        <div className="min-w-0 flex-1 py-3 pl-3">
                          <div className="flex items-center gap-1.5">
                            <Car className="h-4 w-4 shrink-0 text-brand" strokeWidth={1.75} />
                            <p className="text-sm font-bold text-foreground">{t("auth.become_host_title")}</p>
                          </div>
                          <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
                            {t("auth.become_host_desc")}
                          </p>
                        </div>
                        <img
                          src={`${import.meta.env.BASE_URL || "/"}images/become-a-host-car.webp`}
                          alt=""
                          aria-hidden="true"
                          className="h-14 w-20 shrink-0 object-contain"
                          loading="lazy"
                        />
                      </div>
                    </button>
                  </div>

                  <DropdownMenuSeparator className="my-0" />

                  <DropdownMenuGroup className="py-1">
                    {referralsEnabled && (
                      <DropdownMenuItem asChild className="px-4 py-2.5 cursor-pointer">
                        <Link href="/dashboard/referrals" className="flex items-center gap-3">
                          <span className="text-sm">{t("nav.refer_a_host")}</span>
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem asChild className="px-4 py-2.5 cursor-pointer">
                      <Link href="/list-a-car" className="flex items-center gap-3">
                        <span className="text-sm">{t("nav.list_your_car")}</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="px-4 py-2.5 cursor-pointer">
                      <Link href="/search" className="flex items-center gap-3">
                        <span className="text-sm">{t("nav.rent_a_car")}</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={(e) => {
                        e.preventDefault();
                        openHelpCentre();
                      }}
                      className="px-4 py-2.5 cursor-pointer"
                      data-testid="header-help-centre-guest"
                    >
                      <span className="text-sm">{t("nav.help_centre")}</span>
                    </DropdownMenuItem>
                  </DropdownMenuGroup>

                  <DropdownMenuSeparator className="my-0" />

                  <DropdownMenuGroup className="py-1">
                    <DropdownMenuItem
                      onSelect={(e) => {
                        e.preventDefault();
                        authModal.open();
                      }}
                      data-testid="header-login-guest"
                      className="mx-2 my-1 justify-center rounded-lg bg-primary px-4 py-2.5 cursor-pointer text-primary-foreground focus:bg-primary/90 focus:text-primary-foreground data-[highlighted]:bg-primary/90 data-[highlighted]:text-primary-foreground"
                    >
                      <span className="text-sm font-semibold">{t("auth.log_in_or_sign_up")}</span>
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          </div>

          {/* Original mobile-only burger breakpoint. */}
          <button
            type="button"
            data-testid="mobile-nav-toggle"
            className={cn(
              "md:hidden cursor-pointer touch-44 flex h-11 w-11 items-center justify-center rounded-full transition-colors duration-300",
              isTransparent ? "text-white hover:bg-white/10" : "text-white md:text-gray-700 hover:bg-white/10 md:hover:bg-gray-100",
            )}
            onClick={() => setMobileOpen(true)}
            aria-label={t("nav.toggle_menu")}
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <MobileNavigationSheetContent title={t("nav.toggle_menu")}>
            {user ? (
              <>
                <div className="flex items-center gap-3 px-3 py-3 pr-14">
                  <Avatar className="h-10 w-10">
                    {profile?.profile_image && (
                      <AvatarImage src={profile.profile_image} alt={profile.first_name || "User"} />
                    )}
                    <AvatarFallback className="bg-brand text-white text-xs font-semibold">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-sm font-semibold">{profile?.first_name} {profile?.last_name}</p>
                    <p className="text-xs text-gray-500">{profile?.email || user.email}</p>
                  </div>
                </div>
                {isHost ? (
                  <button
                    type="button"
                    onClick={handleToggleMode}
                    className="cursor-pointer flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-base font-semibold text-brand hover:bg-gray-50"
                  >
                    <ArrowRightLeft className="h-5 w-5 text-brand" strokeWidth={1.75} />
                    {inHostMode ? t("nav.switch_to_guest") : t("nav.switch_to_hosting")}
                  </button>
                ) : (
                  <Link href="/list-a-car" className="cursor-pointer flex items-center gap-3 rounded-xl px-3 py-2.5 text-base font-medium text-gray-700 hover:bg-gray-50" onClick={() => setMobileOpen(false)}>
                    <Car className="h-5 w-5 text-gray-500" strokeWidth={1.75} />
                    {t("nav.list_your_car")}
                  </Link>
                )}
                <hr className="my-1 border-gray-100 mx-2" />
                {/* Task #1083: mobile menu mirrors the dropdown's mode
                    split — guest-only links hidden in host mode, host
                    links hidden in guest mode, so the arrow toggle stays
                    the sole in-app mode switcher. */}
                {!inHostMode && (
                  <>
                    <Link href="/trips" className="cursor-pointer flex items-center gap-3 rounded-xl px-3 py-2.5 text-base font-medium text-gray-700 hover:bg-gray-50" onClick={() => setMobileOpen(false)}>
                      <Briefcase className="h-5 w-5 text-gray-500" strokeWidth={1.75} />
                      {t("nav.my_trips")}
                    </Link>
                    <Link href="/favourites" className="cursor-pointer flex items-center gap-3 rounded-xl px-3 py-2.5 text-base font-medium text-gray-700 hover:bg-gray-50" onClick={() => setMobileOpen(false)}>
                      <Heart className="h-5 w-5 text-gray-500" strokeWidth={1.75} />
                      {t("nav.favourites")}
                    </Link>
                  </>
                )}
                {inHostMode && (
                  <>
                    <Link href="/dashboard" className="cursor-pointer flex items-center gap-3 rounded-xl px-3 py-2.5 text-base font-medium text-gray-700 hover:bg-gray-50" onClick={() => setMobileOpen(false)}>
                      <LayoutDashboard className="h-5 w-5 text-gray-500" strokeWidth={1.75} />
                      {t("nav.host_dashboard")}
                    </Link>
                    <Link href="/dashboard/bookings" className="cursor-pointer flex items-center gap-3 rounded-xl px-3 py-2.5 text-base font-medium text-gray-700 hover:bg-gray-50" onClick={() => setMobileOpen(false)}>
                      <ClipboardList className="h-5 w-5 text-gray-500" strokeWidth={1.75} />
                      {t("nav.bookings")}
                    </Link>
                    <Link href="/dashboard/listings" className="cursor-pointer flex items-center gap-3 rounded-xl px-3 py-2.5 text-base font-medium text-gray-700 hover:bg-gray-50" onClick={() => setMobileOpen(false)}>
                      <Car className="h-5 w-5 text-gray-500" strokeWidth={1.75} />
                      {t("nav.my_garage", "My Garage")}
                    </Link>
                    <Link href="/dashboard/earnings" className="cursor-pointer flex items-center gap-3 rounded-xl px-3 py-2.5 text-base font-medium text-gray-700 hover:bg-gray-50" onClick={() => setMobileOpen(false)}>
                      <DollarSign className="h-5 w-5 text-gray-500" strokeWidth={1.75} />
                      {t("nav.earnings")}
                    </Link>
                  </>
                )}
                {messagesHref && (
                <Link href={messagesHref} className="cursor-pointer flex items-center gap-3 rounded-xl px-3 py-2.5 text-base font-medium text-gray-700 hover:bg-gray-50" onClick={() => setMobileOpen(false)}>
                  <MessageSquare className="h-5 w-5 text-gray-500" strokeWidth={1.75} />
                  <span className="flex-1">{t("nav.messages")}</span>
                  {unreadMessages > 0 && (
                    <span className="flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-bold text-white">
                      {unreadMessages}
                    </span>
                  )}
                </Link>
                )}
                <Link href={inHostMode ? "/dashboard/notifications" : "/notifications"} className="cursor-pointer flex items-center gap-3 rounded-xl px-3 py-2.5 text-base font-medium text-gray-700 hover:bg-gray-50" onClick={() => setMobileOpen(false)}>
                  <Bell className="h-5 w-5 text-gray-500" strokeWidth={1.75} />
                  <span className="flex-1">{t("nav.notifications")}</span>
                  {unreadNotifications > 0 && (
                    <span className="flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-bold text-white">
                      {unreadNotifications}
                    </span>
                  )}
                </Link>
                <hr className="my-1 border-gray-100 mx-2" />
                <Link href={inHostMode ? "/dashboard/profile" : "/profile"} className="cursor-pointer flex items-center gap-3 rounded-xl px-3 py-2.5 text-base font-medium text-gray-700 hover:bg-gray-50" onClick={() => setMobileOpen(false)}>
                  <User className="h-5 w-5 text-gray-500" strokeWidth={1.75} />
                  {inHostMode ? t("nav.host_profile") : t("nav.profile")}
                </Link>
                <Link href="/settings" className="cursor-pointer flex items-center gap-3 rounded-xl px-3 py-2.5 text-base font-medium text-gray-700 hover:bg-gray-50" onClick={() => setMobileOpen(false)}>
                  <Settings className="h-5 w-5 text-gray-500" strokeWidth={1.75} />
                  {t("nav.account_settings")}
                </Link>
                {isAdmin && (
                  <Link href="/admin" className="cursor-pointer flex items-center gap-3 rounded-xl px-3 py-2.5 text-base font-medium text-gray-700 hover:bg-gray-50" onClick={() => setMobileOpen(false)}>
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" strokeWidth={1.75} />
                    {t("nav.admin")}
                  </Link>
                )}
                <hr className="my-1 border-gray-100 mx-2" />
                <LanguageSwitcher
                  iconClassName="text-gray-500"
                  buttonClassName="hover:bg-gray-50"
                  align="start"
                  variant="labeled"
                />
                <button
                  onClick={handleSignOut}
                  className="cursor-pointer flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-base font-medium text-gray-700 hover:bg-gray-50"
                >
                  <LogOut className="h-5 w-5 text-gray-500" strokeWidth={1.75} />
                  {t("nav.sign_out")}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  data-testid="mobile-host-signup-card"
                  className="mb-2 block w-full text-left overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow"
                  onClick={() => {
                    setMobileOpen(false);
                    authModal.open({ intent: "signup" });
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className="min-w-0 flex-1 py-4 pl-4">
                      <div className="flex items-center gap-2">
                        <Car className="h-5 w-5 shrink-0 text-primary" strokeWidth={1.75} />
                        <h3 className="text-base font-bold text-gray-900">
                          {t("auth.become_host_title")}
                        </h3>
                      </div>
                      <p className="mt-1 text-sm leading-snug text-gray-600">
                        {t("auth.become_host_desc")}
                      </p>
                    </div>
                    <img
                      src={`${import.meta.env.BASE_URL || "/"}images/become-a-host-car.webp`}
                      alt=""
                      aria-hidden="true"
                      className="h-20 w-32 shrink-0 object-contain"
                      loading="lazy"
                    />
                  </div>
                </button>
                <button
                  type="button"
                  data-testid="mobile-login-guest"
                  className="cursor-pointer block w-full text-center rounded-xl px-3 py-3 text-base font-semibold bg-primary text-primary-foreground hover:bg-primary/90"
                  onClick={() => {
                    setMobileOpen(false);
                    authModal.open();
                  }}
                >
                  {t("auth.log_in_or_sign_up")}
                </button>
                <LanguageSwitcher
                  iconClassName="text-gray-500"
                  buttonClassName="hover:bg-gray-50"
                  align="start"
                  variant="labeled"
                />
                <hr className="my-1 border-gray-100 mx-2" />
                <Link
                  href="/list-a-car"
                  className="cursor-pointer flex items-center gap-3 rounded-xl px-3 py-2.5 text-base font-medium text-gray-700 hover:bg-gray-50"
                  onClick={() => setMobileOpen(false)}
                >
                  <Car className="h-5 w-5 text-gray-500" strokeWidth={1.75} />
                  {t("nav.become_a_host")}
                </Link>
                <button
                  type="button"
                  onClick={openHelpCentre}
                  className="cursor-pointer flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-base font-medium text-gray-700 hover:bg-gray-50"
                  data-testid="header-help-mobile-guest"
                >
                  <HelpCircle className="h-5 w-5 text-gray-500" strokeWidth={1.75} />
                  {t("nav.help")}
                </button>
              </>
            )}
        </MobileNavigationSheetContent>
      </Sheet>
    </header>
  );
}
